import { useState, useEffect } from "react";
import _ from "lodash";

import { Cookies, CookieKeys } from "@ctra/utils";
import { useSpinner } from "@ctra/components";

import { useFarmList } from "@farms";

/**
 * Custom hook to manage farm name display preference.
 * @returns {{useObfuscatedNames: boolean, setUseObfuscatedNames: (value: (((prevState: boolean) => boolean) | boolean)) => void}}
 */
export const useFarmNamePreference = () => {
  const { farmList } = useFarmList();

  const {
    api: { toggle }
  } = useSpinner();

  const [useObfuscatedNames, setUseObfuscatedNames] = useState<boolean>(() => {
    const savedPreference = Cookies.get(CookieKeys.farmNamePreference);

    return _.size(farmList) > 1 && savedPreference === "obfuscated";
  });

  useEffect(() => {
    const savedPreference = Cookies.get(CookieKeys.farmNamePreference);

    if (savedPreference) {
      setUseObfuscatedNames(savedPreference === "obfuscated");
    }
  }, []);

  /**
   * Set the farm name display preference and reload the page to apply changes.
   * @param {boolean | ((prevState: boolean) => boolean)} value
   */
  const setValue = (value: boolean | ((prevState: boolean) => boolean)) => {
    toggle("farmNamePreferences", true);

    Cookies.set(
      CookieKeys.farmNamePreference,
      typeof value === "function"
        ? value(useObfuscatedNames)
          ? "obfuscated"
          : "real"
        : value
        ? "obfuscated"
        : "real"
    );

    setUseObfuscatedNames(value);

    /**
     * Use a fake timeout to allow the spinner to show before reloading
     */
    setTimeout(() => window.location.reload(), 500);
  };

  return {
    useObfuscatedNames,
    setUseObfuscatedNames: setValue
  };
};
